import { useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { AutoActionRuleModal } from "components/risk/AutoActionRuleModal"
import { buildAutoActionColumns } from "components/risk/columns"
import { DataTable } from "components/tables/DataTable"

import { riskService } from "services/risk/risk.service"
import { AutoActionRule } from "services/risk/types"

export const AutoActionsTab = () => {
  const [rules, setRules] = useState<AutoActionRule[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<AutoActionRule | null>(null)

  const loadRules = useCallback(async () => {
    const data = await riskService.listAutoActions()
    if (data) setRules(data)
    setLoading(false)
  }, [])

  useEffect(() => {
    loadRules()
  }, [loadRules])

  const handleToggle = async (rule: AutoActionRule) => {
    await riskService.toggleAutoAction(rule.id)
    await loadRules()
  }

  const handleDelete = async (rule: AutoActionRule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return
    await riskService.deleteAutoAction(rule.id)
    toast.success("Rule deleted")
    await loadRules()
  }

  const handleEdit = (rule: AutoActionRule) => {
    setEditingRule(rule)
    setModalOpen(true)
  }

  const handleCreate = () => {
    setEditingRule(null)
    setModalOpen(true)
  }

  const columns = buildAutoActionColumns({ onToggle: handleToggle, onDelete: handleDelete, onEdit: handleEdit })

  if (loading)
    return (
      <div className="flex justify-center p-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    )

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Auto Action Rules</h3>
        <button className="btn btn-primary btn-sm" onClick={handleCreate}>
          + Create Rule
        </button>
      </div>

      {rules.length === 0 ? (
        <div className="bg-white dark:bg-[#131421] border border-gray-200 dark:border-[rgba(255,255,255,0.1)] shadow-sm dark:shadow-none rounded-lg p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">No auto-action rules configured yet.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-[#131421] border border-gray-200 dark:border-[rgba(255,255,255,0.1)] shadow-sm dark:shadow-none rounded-lg">
          <DataTable rows={rules} columns={columns} rowKey={(r) => r.id} />
        </div>
      )}

      <AutoActionRuleModal rule={editingRule} open={modalOpen} onClose={() => setModalOpen(false)} onSave={loadRules} />
    </div>
  )
}
